import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  HOUR_CHOICES,
  MINUTE_STEPS,
  meridiemOf,
  parseTimeParts,
  toTime24,
  type Meridiem,
} from "@/lib/timeOfDay";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

interface TimeSelectProps {
  value: string;
  onChange: (value: string) => void;
  /** Names the field for screen readers when no aria-label is given. */
  placeholder?: string;
  /**
   * The half of the day to assume before anyone touches AM/PM — an end time
   * follows its start, so a 9 PM social does not offer 10 in the morning.
   */
  defaultMeridiem?: Meridiem;
  "aria-label"?: string;
}

/**
 * Picking a time in the three pieces people say it in.
 *
 * A single list of every quarter hour in a day is ninety-six rows of scrolling
 * to reach the evening, where nearly every dance is. An hour, a minute and a
 * side of noon are twelve, four and two choices instead.
 */
export function TimeSelect({
  value,
  onChange,
  placeholder = "Select time",
  defaultMeridiem = "PM",
  "aria-label": ariaLabel,
}: TimeSelectProps) {
  const parts = parseTimeParts(value);

  // Held only while the field is still empty: an hour has to be chosen before
  // there is a time to report, but the minute and AM/PM picked on the way
  // there should stick.
  const [pendingMinute, setPendingMinute] = useState(0);
  const [pendingMeridiem, setPendingMeridiem] = useState<Meridiem | null>(null);

  const minute = parts?.minute ?? pendingMinute;
  const meridiem = parts?.meridiem ?? pendingMeridiem ?? defaultMeridiem;

  const emit = (next: Partial<{ hour12: number; minute: number; meridiem: Meridiem }>) => {
    // A dropdown that reports no selection must not become a NaN o'clock.
    if (
      (next.hour12 !== undefined && !Number.isFinite(next.hour12)) ||
      (next.minute !== undefined && !Number.isFinite(next.minute))
    ) {
      return;
    }
    const hour12 = next.hour12 ?? parts?.hour12;
    if (hour12 === undefined) {
      // No hour yet — remember the choice and wait for one.
      if (next.minute !== undefined) setPendingMinute(next.minute);
      if (next.meridiem !== undefined) setPendingMeridiem(next.meridiem);
      return;
    }
    onChange(
      toTime24({
        hour12,
        minute: next.minute ?? minute,
        meridiem: next.meridiem ?? meridiem,
      }),
    );
  };

  // Data saved before quarter hours existed can sit off the grid; show it
  // rather than silently snapping someone's 7:20 lesson to 7:15.
  const minuteChoices = MINUTE_STEPS.includes(minute)
    ? MINUTE_STEPS
    : [...MINUTE_STEPS, minute].sort((a, b) => a - b);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Select
        value={parts ? String(parts.hour12) : ""}
        onValueChange={(h) => emit({ hour12: parseInt(h, 10) })}
      >
        <SelectTrigger className="w-[4.5rem] shrink-0" aria-label={ariaLabel ?? placeholder}>
          {/* A dash, not a sentence: there is only room here for a number, and
              the field's own label above already says which time this is. */}
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent>
          {HOUR_CHOICES.map((hour) => (
            <SelectItem key={hour} value={String(hour)}>
              {hour}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <span className="text-muted-foreground text-sm" aria-hidden="true">
        :
      </span>

      <Select value={String(minute)} onValueChange={(m) => emit({ minute: parseInt(m, 10) })}>
        <SelectTrigger className="w-[4.5rem] shrink-0" aria-label="Minutes">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {minuteChoices.map((m) => (
            <SelectItem key={m} value={String(m)}>
              {String(m).padStart(2, "0")}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div
        role="group"
        aria-label="AM or PM"
        className="flex shrink-0 rounded-md border border-input overflow-hidden"
      >
        {(["AM", "PM"] as const).map((m) => (
          <button
            key={m}
            type="button"
            aria-pressed={meridiem === m}
            onClick={() => emit({ meridiem: m })}
            className={cn(
              "px-2 py-2 text-xs font-medium transition-colors",
              meridiem === m
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent",
            )}
          >
            {m}
          </button>
        ))}
      </div>
    </div>
  );
}

/** What an end time should assume when its start is already set. */
export function followMeridiem(startTime: string): Meridiem {
  return meridiemOf(startTime) ?? "PM";
}
